import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import moment from 'moment';
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import toast from 'react-hot-toast';
import userProgramsService from '../../../services/userProgramsService';
import { STATUS_OPTIONS } from '../constants';

const TYPE_OPTIONS = [
  { value: 'official', label: 'Official' },
  { value: 'unofficial', label: 'Unofficial' },
];

export interface TranscriptItem {
  id: string;
  type: string;
  status: string;
  targetDate: string;
  quickLink: string;
  notes: string;
}

const createEmptyItem = (): TranscriptItem => ({
  id: `transcript-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  type: 'official',
  status: 'not_started',
  targetDate: '',
  quickLink: '',
  notes: '',
});

const labelStyle = {
  display: 'block' as const,
  color: '#111827',
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '8px',
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
};

function isValidUrl(url: string): boolean {
  const trimmed = url.trim();
  return trimmed.startsWith('https://') || trimmed.startsWith('http://');
}

interface TranscriptFormItemProps {
  item: TranscriptItem;
  onChange: (item: TranscriptItem) => void;
  onDelete: () => void;
}

const TranscriptFormItem: React.FC<TranscriptFormItemProps> = ({ item, onChange, onDelete }) => {
  const update = useCallback(
    (patch: Partial<TranscriptItem>) => {
      onChange({ ...item, ...patch });
    },
    [item, onChange]
  );

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        backgroundColor: '#fff',
      }}
    >
      {/* Row 1: Target Date | Status */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Target Date</label>
          <DatePicker
            value={item.targetDate?.trim() && moment(item.targetDate).isValid() ? moment(item.targetDate) : null}
            onChange={(val) => update({ targetDate: val && moment(val).isValid() ? moment(val).format('YYYY-MM-DD') : '' })}
            inputFormat="MM/DD/yyyy"
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" placeholder="MM/DD/yyyy" sx={inputSx} />
            )}
          />
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <FormControl fullWidth size="small">
            <Select
              value={item.status}
              onChange={(e: SelectChangeEvent<string>) => update({ status: e.target.value })}
              IconComponent={KeyboardArrowDownIcon}
              sx={{ borderRadius: '8px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } }}
              variant="outlined"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Row 2: Type */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Type</label>
        <FormControl fullWidth size="small">
          <Select
            value={item.type}
            onChange={(e: SelectChangeEvent<string>) => update({ type: e.target.value })}
            IconComponent={KeyboardArrowDownIcon}
            sx={{ borderRadius: '8px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } }}
            variant="outlined"
          >
            {TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Row 3: Quick Link & Notes */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Quick Link & Notes</label>
        <TextField
          fullWidth
          size="small"
          value={item.quickLink}
          onChange={(e) => update({ quickLink: e.target.value })}
          placeholder="Add link..."
          variant="outlined"
          hiddenLabel
          error={!!item.quickLink?.trim() && !isValidUrl(item.quickLink)}
          helperText={
            item.quickLink?.trim() && !isValidUrl(item.quickLink)
              ? 'Link must start with https:// or http://'
              : undefined
          }
          sx={{ ...inputSx, mb: 1 }}
        />
        {item.quickLink?.trim() && isValidUrl(item.quickLink) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <a
              href={item.quickLink.trim()}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              <LinkIcon sx={{ fontSize: '18px' }} /> View Link
            </a>
          </div>
        )}
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          value={item.notes}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Add notes..."
          variant="outlined"
          hiddenLabel
          sx={inputSx}
        />
      </div>

      {/* Row 4: Delete */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Button
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
          sx={{
            color: '#dc2626',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

interface TranscriptsDropdownProps {
  onClose?: () => void;
  onSubmit?: (items: TranscriptItem[]) => void;
  initialItems?: TranscriptItem[];
  addTranscriptTrigger?: number;
  /** ID từ details.programManager.id — khi có thì Save gọi PATCH /api/programs-manager/:id với transcripts */
  programManagerId?: number;
  /** Master deadline từ Application Form; dùng làm default cho targetDate khi rỗng */
  applicationDeadline?: string;
}

const TranscriptsDropdown: React.FC<TranscriptsDropdownProps> = ({
  onClose,
  onSubmit,
  initialItems,
  addTranscriptTrigger = 0,
  programManagerId,
  applicationDeadline = '',
}) => {
  const [items, setItems] = useState<TranscriptItem[]>(() =>
    initialItems?.length
      ? initialItems.map((it) => ({ ...createEmptyItem(), ...it }))
      : []
  );
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const prevTriggerRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const programManagerIdRef = useRef(programManagerId);
  programManagerIdRef.current = programManagerId;
  const isFirstRender = useRef(true);

  const debouncedAutoSave = useMemo(
    () =>
      debounce(() => {
        const id = programManagerIdRef.current;
        if (id == null) return;
        const current = itemsRef.current;
        if (current.some((it) => it.targetDate?.trim() && isNaN(new Date(it.targetDate).getTime()))) return;
        if (current.some((it) => it.quickLink?.trim() && !isValidUrl(it.quickLink))) return;
        const transcripts = current.map((it) => ({
          type: it.type,
          status: it.status,
          date: it.targetDate || '',
          link: it.quickLink || undefined,
          notes: it.notes || undefined,
        }));
        userProgramsService
          .patchProgramsManager(id, { transcripts })
          .then(() => {
            setAutoSaved(true);
            toast.success(current.length === 1 ? '1 transcript saved.' : `${current.length} transcripts saved.`);
          })
          .catch(() => {});
      }, 800),
    []
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setAutoSaved(false);
    debouncedAutoSave();
    return () => debouncedAutoSave.cancel();
  }, [items, debouncedAutoSave]);

  const createItemWithDeadline = useCallback(() => {
    const item = createEmptyItem();
    if (applicationDeadline?.trim()) item.targetDate = applicationDeadline.trim();
    return item;
  }, [applicationDeadline]);

  useEffect(() => {
    if (addTranscriptTrigger > prevTriggerRef.current) {
      const count = addTranscriptTrigger - prevTriggerRef.current;
      setItems((prev) => [...prev, ...Array.from({ length: count }, createItemWithDeadline)]);
      prevTriggerRef.current = addTranscriptTrigger;
    }
  }, [addTranscriptTrigger, createItemWithDeadline]);

  const handleChange = useCallback((index: number, updated: TranscriptItem) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  const handleDelete = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    const invalidDateIndex = items.findIndex(
      (it) => it.targetDate.trim() && isNaN(new Date(it.targetDate.trim()).getTime())
    );
    if (invalidDateIndex !== -1) {
      toast.error(`Please enter a valid Target Date in item ${invalidDateIndex + 1}.`);
      return;
    }

    const invalidLinkIndex = items.findIndex(
      (it) => it.quickLink?.trim() && !isValidUrl(it.quickLink)
    );
    if (invalidLinkIndex !== -1) {
      toast.error(`Please enter a valid link (must start with https:// or http://) in item ${invalidLinkIndex + 1}.`);
      return;
    }

    const transcripts = items.map((it) => ({
      type: it.type,
      status: it.status,
      date: it.targetDate || '',
      link: it.quickLink || undefined,
      notes: it.notes || undefined,
    }));

    if (programManagerId != null) {
      setSaving(true);
      try {
        await userProgramsService.patchProgramsManager(programManagerId, {
          transcripts,
        });
        const count = items.length;
        toast.success(
          count === 1 ? '1 transcript saved.' : `${count} transcripts saved.`
        );
        onSubmit?.(items);
      } catch {
        // Error toast handled by http interceptor
      } finally {
        setSaving(false);
      }
    } else {
      onSubmit?.(items);
      onClose?.();
    }
  }, [items, programManagerId, onSubmit, onClose]);

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginTop: '-12px',
        marginBottom: '12px',
      }}
    >
      {items.length === 0 ? (
        <div style={{ color: '#6b7280', fontSize: '14px', padding: '16px 0', textAlign: 'center' }}>
          Click &quot;+ Add Transcripts&quot; above to add a transcript.
        </div>
      ) : (
        items.map((item, index) => (
          <TranscriptFormItem
            key={item.id}
            item={item}
            onChange={(updated) => handleChange(index, updated)}
            onDelete={() => handleDelete(index)}
          />
        ))
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
        {programManagerId != null && autoSaved && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircleIcon sx={{ fontSize: '18px', color: '#16a34a' }} />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Auto-saved</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
          <Button onClick={onClose} sx={{ color: '#6b7280', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ bgcolor: '#2563eb', fontWeight: 600 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TranscriptsDropdown;
